import { t } from './i18n';
import './elements/vitesse-members-panel';
/*
 * OUR members screen, mounted as a page of the provider's own profile dialog.
 *
 * The provider ships one already, and this does not replace what it KNOWS — every
 * read and every write below is still its call. What it replaces is the chrome:
 * its table, its search, its rows, its button. Theming those from outside never
 * settled, because the parts that shift are its async loads landing inside a
 * layout we do not own.
 *
 * Registered as a custom page rather than by patching the built-in, because a
 * page we mount is a page we control end to end — no selector can be renamed out
 * from under it by a provider release.
 */
const MEMBERS_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
/*
 * A LAST-KNOWN roster, module-scoped, so re-opening the People page paints
 * instantly instead of blanking for a provider round-trip every time. The dialog
 * only ever shows one org at a time, so one slot is enough; a fresh read always
 * runs on mount and overwrites it, so the cache is a head-start, never the
 * source of truth (stale-while-revalidate).
 */
let lastRoster;
export function membersPage(opts) {
    let panel;
    const loadInvitations = async () => {
        const org = opts.organization();
        if (!org?.getInvitations)
            return [];
        const page = (await org.getInvitations({ pageSize: 100 }));
        const rows = Array.isArray(page) ? page : (page?.data ?? []);
        /*
         * PENDING ONLY.
         *
         * The provider returns accepted and revoked invitations alongside pending
         * ones, so an unfiltered list showed a colleague who is already IN the org as
         * still "invited as org:admin" — two rows for one person saying different
         * things. An invitation that has been answered is history, and this screen is
         * the list of people still waiting on one.
         */
        return rows
            .filter((i) => !i.status || i.status === 'pending')
            .map((i) => ({
            id: i.id ?? '',
            email: i.emailAddress ?? '',
            role: i.role ?? '',
            createdAtMs: i.createdAt ? new Date(i.createdAt).getTime() : 0,
            // The revoke handle rides ALONG with the row rather than being looked up
            // again later: the provider's object is the only thing that can revoke it,
            // and re-finding it by id would be a second read that can disagree.
            _raw: i,
        }));
    };
    const refresh = async () => {
        if (!panel)
            return;
        const p = panel;
        // The two reads are independent and each has its own tab count, so neither
        // waits on the other — the roster (the main content) paints the moment its
        // read lands rather than blocking on the slower invitations call. Each also
        // updates the shared cache so the next open of this page starts populated.
        void Promise.resolve(opts.members())
            .catch(() => [])
            .then((members) => {
            p.members = members;
            lastRoster = { members, invitations: lastRoster?.invitations ?? [] };
        });
        void loadInvitations()
            .catch(() => [])
            .then((invitations) => {
            p.invitations = invitations;
            lastRoster = { members: lastRoster?.members ?? p.members, invitations };
        });
    };
    return {
        label: t('members.tabMembers'),
        url: 'people',
        mountIcon: (el) => {
            el.innerHTML = MEMBERS_ICON;
        },
        unmountIcon: () => { },
        mount: (el) => {
            panel = document.createElement('vitesse-members-panel');
            panel.canManage = opts.canManage?.() ?? true;
            panel.roleOptions = opts.roleOptions();
            if (opts.setOrgRole)
                panel.setRole = (m, role) => opts.setOrgRole(m, role);
            if (opts.removeMember)
                panel.removeMember = (m) => opts.removeMember(m);
            panel.invite = async (email, role, expiresInDays) => {
                const org = opts.organization();
                if (!org?.inviteMember)
                    throw new Error(t('members.inviteUnavailable'));
                // Always a real limit now — the picker no longer offers "whatever the
                // provider does", so there is no case where this is left unsaid. Still
                // guarded against a zero: the provider reads an explicit 0 as "expires
                // immediately", which would send invitations that are dead on arrival.
                //
                // The role crosses a boundary here. Everything above speaks the bare word
                // ('admin'), because that is what normalizeRole() produces on the way in and
                // what the picker renders. The provider's wire form is PREFIXED, and it
                // answers an unprefixed role with "Organization role not found" — so the
                // prefix goes on at the write, exactly as setOrgRole() does it. An id that
                // already carries a prefix is passed through, so a custom role authored as
                // 'org:auditor' still works.
                await org.inviteMember({
                    emailAddress: email,
                    role: role.includes(':') ? role : `org:${role}`,
                    ...(expiresInDays > 0 ? { expiresInDays } : {}),
                });
            };
            panel.revokeInvitation = async (inv) => {
                const raw = inv._raw;
                if (!raw?.revoke)
                    throw new Error(t('members.revokeUnavailable'));
                await raw.revoke();
            };
            // After any mutation the panel asks for a re-read rather than patching its
            // own copy — the provider is the source of truth and a local patch is a
            // second opinion that can be wrong.
            panel.addEventListener('members-changed', () => void refresh());
            // Paint last-known rows immediately so re-entry is instant; the refresh
            // below then revalidates against the provider in the background.
            if (lastRoster) {
                panel.members = lastRoster.members;
                panel.invitations = lastRoster.invitations;
            }
            el.appendChild(panel);
            void refresh();
        },
        unmount: () => {
            panel?.remove();
            panel = undefined;
        },
    };
}
//# sourceMappingURL=members-page.js.map